import React, { useState, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import Confetti from './Confetti'

interface RaffleWidgetProps {
  discordId: string
  endTime?: string // Optional, not used anymore - API provides end time
}

interface RaffleData {
  id: string
  name: string
  description: string
  prize: string
  countdown: {
    timeUntilEnd: number
    timeDisplay: string
    isActive: boolean
    scheduledAt: string
  }
  status: string
  createdAt: string
}

interface MonthlyRaffleData {
  participantCount: number
  nextRaffleDate: string
  description: string
  timeUntilEnd: number
  timeDisplay: string
}

interface MonthlyRaffleParticipant {
  id: string
  discordId: string
  createdAt: string
  user: {
    id: string
    discordId: string
    username: string | null
    discriminator: string | null
    avatar: string | null
    createdAt: string
    updatedAt: string
  }
}

interface MonthlyRaffleParticipantsResponse {
  participants: MonthlyRaffleParticipant[]
  totalCount: number
}

// Function to get correct Russian declension for time units
const getRussianDeclension = (count: number, forms: [string, string, string]): string => {
  const lastDigit = count % 10
  const lastTwoDigits = count % 100
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return forms[2] // для 11-19 всегда третья форма
  }
  
  if (lastDigit === 1) {
    return forms[0] // для 1, 21, 31, 41, 51, 61, 71, 81, 91
  }
  
  if (lastDigit >= 2 && lastDigit <= 4) {
    return forms[1] // для 2-4, 22-24, 32-34, 42-44, 52-54, 62-64, 72-74, 82-84, 92-94
  }
  
  return forms[2] // для остальных случаев (0, 5-9, 10, 20, 25-29, 30, 35-39, 40, 45-49, 50, 55-59, 60, 65-69, 70, 75-79, 80, 85-89, 90, 95-99)
}

const RaffleWidget: React.FC<RaffleWidgetProps> = ({ discordId }) => {
  const [currentRaffle, setCurrentRaffle] = useState<RaffleData | null>(null)
  const [isParticipating, setIsParticipating] = useState<boolean | null>(null)
  const [message, setMessage] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [timeLeft, setTimeLeft] = useState<{
    days: number
    hours: number
    minutes: number
    seconds: number
  }>({ days: 0, hours: 0, minutes: 0, seconds: 0 })
  const [isCompleted, setIsCompleted] = useState<boolean>(false)
  const [initialCountdown, setInitialCountdown] = useState<{
    days: number
    hours: number
    minutes: number
    seconds: number
  } | null>(null)
  const [participantsCount, setParticipantsCount] = useState<number>(0)
  const [showConfetti, setShowConfetti] = useState<boolean>(false)

  // Monthly raffle states
  const [monthlyRaffle, setMonthlyRaffle] = useState<MonthlyRaffleData | null>(null)
  const [isParticipatingMonthly, setIsParticipatingMonthly] = useState<boolean | null>(null)
  const [isLoadingMonthly, setIsLoadingMonthly] = useState(false)
  const [monthlyTimeLeft, setMonthlyTimeLeft] = useState<{
    days: number
    hours: number
    minutes: number
    seconds: number
  }>({ days: 0, hours: 0, minutes: 0, seconds: 0 })
  const [isMonthlyCompleted, setIsMonthlyCompleted] = useState<boolean>(false)
  const [initialMonthlyCountdown, setInitialMonthlyCountdown] = useState<{
    days: number
    hours: number
    minutes: number
    seconds: number
  } | null>(null)
  const [monthlyParticipantsCount, setMonthlyParticipantsCount] = useState<number>(0)
  const [showMonthlyConfetti, setShowMonthlyConfetti] = useState<boolean>(false)
  const [isFamilyMember, setIsFamilyMember] = useState<boolean>(false)

  // Initialize raffle data function
  const initializeRaffle = useCallback(async () => {
    try {
      // First, get current raffle (without participants)
      const raffleResponse = await fetch('https://yasha.ooguy.com:8698/api/raffles/current/open')

      if (!raffleResponse.ok) {
        if (raffleResponse.status === 404) {
          // No active raffle
          setCurrentRaffle(null)
          setIsParticipating(false)
          setMessage('Сейчас ничего не разыгрывается')
          return
        }
        throw new Error(`HTTP error! status: ${raffleResponse.status}`)
      }

      const raffleData: RaffleData = await raffleResponse.json()
      setCurrentRaffle(raffleData)

      // Store initial countdown from API
      if (raffleData.countdown) {
        const { timeUntilEnd, isActive } = raffleData.countdown

        if (isActive && timeUntilEnd > 0) {
          // Calculate initial time from milliseconds
          const totalSeconds = Math.floor(timeUntilEnd / 1000)
          const days = Math.floor(totalSeconds / (24 * 60 * 60))
          const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60))
          const minutes = Math.floor((totalSeconds % (60 * 60)) / 60)
          const seconds = totalSeconds % 60

          const initialTime = { days, hours, minutes, seconds }
          setInitialCountdown(initialTime)
          setTimeLeft(initialTime)
          setIsCompleted(false)

          
        } else {
          // Raffle is not active or time is up
          setInitialCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 })
          setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 })
          setIsCompleted(true)

          
        }
      } else {
        // No countdown data
        setInitialCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 })
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 })
        setIsCompleted(true)
      }

      // Get participants list
      const participantsResponse = await fetch('https://yasha.ooguy.com:8698/api/raffles/current/participants')

      

      if (participantsResponse.ok) {
          const participantsData = await participantsResponse.json()


          // Extract participants array from response
          const participants = participantsData.participants || []

          

          const totalCount = participantsData.totalCount || participants.length
          setParticipantsCount(totalCount)

          // Check if user is participating - handle new API structure
          const isUserParticipating = participants.some((participant: any) => {
            // Handle different possible structures:
            // 1. Direct discordId field
            // 2. user.discordId field
            // 3. participant itself is a string
            const participantDiscordId = participant.user?.discordId || participant.discordId || participant || ''
            const isMatch = String(participantDiscordId) === String(discordId)

            
            return isMatch
          })



          setIsParticipating(isUserParticipating)
          setMessage(isUserParticipating ? 'Вы уже участвуете!' : 'Присоединяйтесь к розыгрышу!')
        } else {
          console.error('Failed to get participants, status:', participantsResponse.status)
          // If can't get participants, assume not participating
          setIsParticipating(false)
          setMessage('Присоединяйтесь к розыгрышу!')
        }

    } catch (error) {
      console.error('Error initializing raffle:', error)
      // Fallback to no raffle state
      setCurrentRaffle(null)
      setIsParticipating(false)
      setMessage('Сейчас ничего не разыгрывается')
    }
  }, [discordId])

  // Initialize monthly raffle data function
  const initializeMonthlyRaffle = useCallback(async () => {
    try {
      // Get monthly raffle info
      const monthlyInfoResponse = await fetch('https://yasha.ooguy.com:8698/api/monthly-raffle/info')

      if (!monthlyInfoResponse.ok) {
        if (monthlyInfoResponse.status === 404) {
          setMonthlyRaffle(null)
          setIsParticipatingMonthly(false)
          return
        }
        throw new Error(`HTTP error! status: ${monthlyInfoResponse.status}`)
      }

      const monthlyData: MonthlyRaffleData = await monthlyInfoResponse.json()
      setMonthlyRaffle(monthlyData)
      setMonthlyParticipantsCount(monthlyData.participantCount)

      // Calculate initial countdown from milliseconds
      if (monthlyData.timeUntilEnd > 0) {
        const totalSeconds = Math.floor(monthlyData.timeUntilEnd / 1000)
        const days = Math.floor(totalSeconds / (24 * 60 * 60))
        const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60))
        const minutes = Math.floor((totalSeconds % (60 * 60)) / 60)
        const seconds = totalSeconds % 60

        const initialTime = { days, hours, minutes, seconds }
        setInitialMonthlyCountdown(initialTime)
        setMonthlyTimeLeft(initialTime)
        setIsMonthlyCompleted(false)
      } else {
        setInitialMonthlyCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 })
        setMonthlyTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 })
        setIsMonthlyCompleted(true)
      }

      // Get monthly participants list
      const monthlyParticipantsResponse = await fetch('https://yasha.ooguy.com:8698/api/monthly-raffle/participants')

      if (monthlyParticipantsResponse.ok) {
        const participantsData: MonthlyRaffleParticipantsResponse = await monthlyParticipantsResponse.json()
        const participants = participantsData.participants || []
        const totalCount = participantsData.totalCount || participants.length
        setMonthlyParticipantsCount(totalCount)

        // Check if user is participating
        const isUserParticipating = participants.some((participant: MonthlyRaffleParticipant) => {
          const participantDiscordId = participant.user?.discordId || participant.discordId || ''
          return String(participantDiscordId) === String(discordId)
        })

        setIsParticipatingMonthly(isUserParticipating)
      } else {
        console.error('Failed to get monthly participants, status:', monthlyParticipantsResponse.status)
        setIsParticipatingMonthly(false)
      }

    } catch (error) {
      console.error('Error initializing monthly raffle:', error)
      setMonthlyRaffle(null)
      setIsParticipatingMonthly(false)
    }
  }, [discordId])

  // Check family membership (placeholder - replace with actual API call)
  const checkFamilyMembership = useCallback(async () => {
    // TODO: Replace with actual API call to check if user is family member
    // For now, we'll assume all users are family members for testing
    setIsFamilyMember(true)
  }, [])

  // Initialize raffle data on mount
  useEffect(() => {
    initializeRaffle()
    initializeMonthlyRaffle()
    checkFamilyMembership()
  }, [initializeRaffle, initializeMonthlyRaffle, checkFamilyMembership])

  // Countdown timer - counts down from API values
  useEffect(() => {
    if (!initialCountdown) return

    let currentTime = { ...initialCountdown }

    const timer = setInterval(() => {
      // Decrease seconds
      if (currentTime.seconds > 0) {
        currentTime.seconds--
      } else {
        // Move to next minute
        currentTime.seconds = 59
        if (currentTime.minutes > 0) {
          currentTime.minutes--
        } else {
          // Move to next hour
          currentTime.minutes = 59
          if (currentTime.hours > 0) {
            currentTime.hours--
          } else {
            // Move to next day
            currentTime.hours = 23
            if (currentTime.days > 0) {
              currentTime.days--
            } else {
              // Time is up
              setIsCompleted(true)
              setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 })
              clearInterval(timer)
              return
            }
          }
        }
      }

      setTimeLeft({ ...currentTime })
    }, 1000)

    return () => clearInterval(timer)
  }, [initialCountdown])

  // Monthly countdown timer
  useEffect(() => {
    if (!initialMonthlyCountdown) return

    let currentTime = { ...initialMonthlyCountdown }

    const timer = setInterval(() => {
      if (currentTime.seconds > 0) {
        currentTime.seconds--
      } else {
        currentTime.seconds = 59
        if (currentTime.minutes > 0) {
          currentTime.minutes--
        } else {
          currentTime.minutes = 59
          if (currentTime.hours > 0) {
            currentTime.hours--
          } else {
            currentTime.hours = 23
            if (currentTime.days > 0) {
              currentTime.days--
            } else {
              setIsMonthlyCompleted(true)
              setMonthlyTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 })
              clearInterval(timer)
              return
            }
          }
        }
      }

      setMonthlyTimeLeft({ ...currentTime })
    }, 1000)

    return () => clearInterval(timer)
  }, [initialMonthlyCountdown])

  // Join raffle
  const handleJoinRaffle = async () => {
    if (isParticipating || isLoading || !currentRaffle || isCompleted) return

    setIsLoading(true)
    try {
  

      const response = await fetch('https://yasha.ooguy.com:8698/api/raffles/current/participants', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ discordId }),
      })

      

      if (!response.ok) {
        if (response.status === 409) {
          // Already participating

          setIsParticipating(true)
          setMessage('Вы уже участвуете!')
          return
        }
        console.error('Join failed with status:', response.status)
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      

      // Update local state
      setIsParticipating(true)
      setMessage('Вы участвуете!')
      setParticipantsCount(prev => prev + 1)

      // Show confetti animation
      setShowConfetti(true)
      // Hide confetti after 5 seconds
      setTimeout(() => setShowConfetti(false), 5000)

      

      // Force refresh participants list after a short delay to ensure consistency
      setTimeout(() => {

        initializeRaffle()
      }, 2000)

    } catch (error) {
      console.error('Error joining raffle:', error)
      setMessage('Ошибка при участии. Попробуйте позже.')
    } finally {
      setIsLoading(false)
    }
  }

  // Join monthly raffle
  const handleJoinMonthlyRaffle = async () => {
    if (isParticipatingMonthly || isLoadingMonthly || !monthlyRaffle || isMonthlyCompleted || !isFamilyMember) return

    setIsLoadingMonthly(true)
    try {
      const response = await fetch('https://yasha.ooguy.com:8698/api/monthly-raffle/participants', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ discordId }),
      })

      if (!response.ok) {
        if (response.status === 409) {
          setIsParticipatingMonthly(true)
          return
        }
        console.error('Monthly join failed with status:', response.status)
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      // Update local state
      setIsParticipatingMonthly(true)
      setMonthlyParticipantsCount(prev => prev + 1)

      // Show confetti animation
      setShowMonthlyConfetti(true)
      setTimeout(() => setShowMonthlyConfetti(false), 5000)

      // Force refresh participants list after a short delay
      setTimeout(() => {
        initializeMonthlyRaffle()
      }, 2000)

    } catch (error) {
      console.error('Error joining monthly raffle:', error)
    } finally {
      setIsLoadingMonthly(false)
    }
  }

  // Loading state
  if (isParticipating === null) {
    return (
      <div className="flex flex-col lg:flex-row gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex-1 bg-gradient-to-br from-dark-800/80 to-dark-900/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/10 shadow-2xl"
        >
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-emerald-400"></div>
            <span className="ml-2 sm:ml-3 text-gray-300 text-sm sm:text-base">Загрузка...</span>
          </div>
        </motion.div>
      </div>
    )
  }

  // No active raffle state
  if (!currentRaffle || message === 'Сейчас ничего не разыгрывается') {
    return (
      <div className="flex flex-col lg:flex-row gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex-1 bg-gradient-to-br from-dark-800/80 to-dark-900/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/10 shadow-2xl relative overflow-hidden"
        >
        {/* Decorative background elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-16 h-56 w-56 rounded-full bg-gray-500/15 blur-3xl" />
          <div className="absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-gray-400/10 blur-3xl" />
        </div>

        <div className="relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="text-4xl sm:text-6xl mb-4 sm:mb-6"
          >
            🎭
          </motion.div>
          
          <motion.h3
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-gray-300 to-gray-400 bg-clip-text text-transparent mb-3 sm:mb-4"
          >
            Розыгрыш временно приостановлен
          </motion.h3>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-gray-400 text-sm sm:text-base lg:text-lg mb-4"
          >
            Сейчас ничего не разыгрывается. Следите за обновлениями!
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-xs sm:text-sm text-gray-300 mb-4 sm:mb-6 hidden sm:block"
          >
            👥 Участников в последнем розыгрыше: {participantsCount}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="inline-block px-4 sm:px-6 py-2 sm:py-3 rounded-full bg-gray-500/20 border border-gray-400/30 text-gray-300 text-xs sm:text-sm font-medium"
          >
            🕐 Ожидание нового розыгрыша
          </motion.div>
        </div>
      </motion.div>
      </div>
    )
  }

  return (
    <>
      {/* Confetti overlay */}
      <Confetti isActive={showConfetti} />
      <Confetti isActive={showMonthlyConfetti} />

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Main Raffle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex-1 bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl rounded-3xl p-6 lg:p-8 border border-slate-600/20 shadow-2xl relative overflow-hidden group hover:border-emerald-500/30 transition-all duration-500 flex flex-col"
        >
          {/* Decorative background elements */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-20 -right-16 h-56 w-56 rounded-full bg-emerald-500/20 blur-3xl group-hover:bg-emerald-400/30 transition-all duration-700" />
            <div className="absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-cyan-500/15 blur-3xl group-hover:bg-cyan-400/25 transition-all duration-700" />
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-32 w-32 rounded-full bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 blur-2xl" />
          </div>

      <div className="relative z-10 grid grid-rows-[140px_120px_80px_80px] gap-4 h-full">
        {/* Header Container */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-center flex flex-col items-center justify-center"
        >
          <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight bg-gradient-to-r from-emerald-300 via-cyan-300 to-emerald-400 bg-clip-text text-transparent mb-2">
            {currentRaffle?.name || 'Еженедельный розыгрыш на 200.000$'}
          </h3>
          <p className="text-slate-300 mb-2 text-sm sm:text-base font-medium">{currentRaffle?.description || 'До окончания осталось:'}</p>
          {currentRaffle?.prize && (
            <div className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border border-yellow-400/40 text-yellow-200 text-sm font-semibold shadow-lg">
              🎁 Приз: {currentRaffle.prize}
            </div>
          )}
        </motion.div>

        {/* Timer Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="flex items-center justify-center gap-3 text-white font-mono select-none"
        >
          {isCompleted ? (
            <div className="px-8 py-4 rounded-2xl bg-gradient-to-r from-red-500/20 to-red-600/20 border border-red-400/40 shadow-lg">
              <div className="text-2xl font-bold text-red-200">Завершен</div>
              <div className="text-sm text-red-300 mt-1">Розыгрыш окончен</div>
            </div>
          ) : (
            <>
              <div className="min-w-[70px] px-4 py-3 rounded-2xl bg-gradient-to-b from-slate-700/50 to-slate-800/50 border border-slate-600/30 shadow-lg backdrop-blur-sm">
                <div className="text-3xl lg:text-4xl font-bold tabular-nums text-emerald-300">{String(timeLeft.days).padStart(2, '0')}</div>
                <div className="text-xs text-slate-300 mt-1 font-medium">{getRussianDeclension(timeLeft.days, ['день', 'дня', 'дней'])}</div>
              </div>
              <div className="text-2xl lg:text-3xl text-emerald-400 pb-3 animate-pulse font-bold">:</div>
              <div className="min-w-[70px] px-4 py-3 rounded-2xl bg-gradient-to-b from-slate-700/50 to-slate-800/50 border border-slate-600/30 shadow-lg backdrop-blur-sm">
                <div className="text-3xl lg:text-4xl font-bold tabular-nums text-cyan-300">{String(timeLeft.hours).padStart(2, '0')}</div>
                <div className="text-xs text-slate-300 mt-1 font-medium">{getRussianDeclension(timeLeft.hours, ['час', 'часа', 'часов'])}</div>
              </div>
              <div className="text-2xl lg:text-3xl text-emerald-400 pb-3 animate-pulse delay-100 font-bold">:</div>
              <div className="min-w-[70px] px-4 py-3 rounded-2xl bg-gradient-to-b from-slate-700/50 to-slate-800/50 border border-slate-600/30 shadow-lg backdrop-blur-sm">
                <div className="text-3xl lg:text-4xl font-bold tabular-nums text-emerald-300">{String(timeLeft.minutes).padStart(2, '0')}</div>
                <div className="text-xs text-slate-300 mt-1 font-medium">минут</div>
              </div>
              <div className="text-2xl lg:text-3xl text-emerald-400 pb-3 animate-pulse delay-200 font-bold">:</div>
              <div className="min-w-[70px] px-4 py-3 rounded-2xl bg-gradient-to-b from-slate-700/50 to-slate-800/50 border border-slate-600/30 shadow-lg backdrop-blur-sm">
                <div className="text-3xl lg:text-4xl font-bold tabular-nums text-cyan-300">{String(timeLeft.seconds).padStart(2, '0')}</div>
                <div className="text-xs text-slate-300 mt-1 font-medium">секунд</div>
              </div>
            </>
          )}
        </motion.div>

        {/* Button Container */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex items-center justify-center"
        >
          <motion.button
            whileHover={{
              scale: (isParticipating || isCompleted || isLoading) ? 1.0 : 1.05,
              y: (isParticipating || isCompleted || isLoading) ? 0 : -2
            }}
            whileTap={{ scale: (isParticipating || isCompleted || isLoading) ? 1.0 : 0.97 }}
            disabled={isParticipating || isLoading || isCompleted}
            onClick={handleJoinRaffle}
            className={`relative px-8 lg:px-12 py-4 lg:py-5 rounded-2xl font-bold text-lg lg:text-xl transition-all duration-300 shadow-xl hover:shadow-2xl focus:outline-none focus:ring-2 focus:ring-emerald-400/50 ${
              isCompleted
                ? 'bg-gradient-to-r from-slate-600/30 to-slate-700/30 text-slate-400 border border-slate-600/30'
                : isParticipating
                  ? 'bg-gradient-to-r from-slate-600/40 to-slate-700/40 text-slate-300 border border-slate-500/40'
                  : 'bg-gradient-to-r from-emerald-500 via-emerald-600 to-cyan-500 text-white hover:from-emerald-600 hover:via-emerald-700 hover:to-cyan-600 border border-emerald-400/30 hover:border-emerald-300/50'
            }`}
          >
            <span className="mr-2 sm:mr-3">🎟️</span>
            {isCompleted
              ? 'Розыгрыш завершен'
              : isLoading
                ? 'Отправка...'
                : isParticipating
                  ? 'Вы уже участвуете'
                  : 'Участвовать'
            }
            {!isParticipating && !isLoading && !isCompleted && (
              <span className="absolute inset-0 rounded-xl sm:rounded-2xl ring-1 ring-white/10" />
            )}
          </motion.button>
        </motion.div>

        {/* Participants Container */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="flex items-center justify-center"
        >
          <div className="text-sm text-slate-300 font-medium">
            👥 Участников: <span className="text-emerald-300 font-bold">{participantsCount}</span>
          </div>
        </motion.div>
      </div>
    </motion.div>

        {/* Monthly Raffle */}
        {monthlyRaffle && isFamilyMember && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex-1 bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl rounded-3xl p-6 lg:p-8 border border-slate-600/20 shadow-2xl relative overflow-hidden group hover:border-purple-500/30 transition-all duration-500 flex flex-col"
          >
            {/* Decorative background elements */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute -top-20 -right-16 h-56 w-56 rounded-full bg-purple-500/20 blur-3xl group-hover:bg-purple-400/30 transition-all duration-700" />
              <div className="absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-indigo-500/15 blur-3xl group-hover:bg-indigo-400/25 transition-all duration-700" />
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-32 w-32 rounded-full bg-gradient-to-r from-purple-500/10 to-indigo-500/10 blur-2xl" />
            </div>

        <div className="relative z-10 grid grid-rows-[140px_120px_80px_80px] gap-4 h-full">
          {/* Header Container */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-center flex flex-col items-center justify-center"
          >
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight bg-gradient-to-r from-purple-300 via-pink-300 to-indigo-300 bg-clip-text text-transparent mb-3">
              Дополнительный розыгрыш
            </h3>
            <div className="text-purple-200 text-sm font-semibold">
              🎁 Призы на выбор: Игра в Steam до 550₽, Majestic Premium 30 дней, или Majestic Coins
            </div>
          </motion.div>

          {/* Timer Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
            className="flex items-center justify-center gap-3 text-white font-mono select-none"
          >
            {isMonthlyCompleted ? (
              <div className="px-8 py-4 rounded-2xl bg-gradient-to-r from-red-500/20 to-red-600/20 border border-red-400/40 shadow-lg">
                <div className="text-2xl font-bold text-red-200">Завершен</div>
                <div className="text-sm text-red-300 mt-1">Розыгрыш окончен</div>
              </div>
            ) : (
              <>
                <div className="min-w-[70px] px-4 py-3 rounded-2xl bg-gradient-to-b from-slate-700/50 to-slate-800/50 border border-slate-600/30 shadow-lg backdrop-blur-sm">
                  <div className="text-3xl lg:text-4xl font-bold tabular-nums text-purple-300">{String(monthlyTimeLeft.days).padStart(2, '0')}</div>
                  <div className="text-xs text-slate-300 mt-1 font-medium">{getRussianDeclension(monthlyTimeLeft.days, ['день', 'дня', 'дней'])}</div>
                </div>
                <div className="text-2xl lg:text-3xl text-purple-400 pb-3 animate-pulse font-bold">:</div>
                <div className="min-w-[70px] px-4 py-3 rounded-2xl bg-gradient-to-b from-slate-700/50 to-slate-800/50 border border-slate-600/30 shadow-lg backdrop-blur-sm">
                  <div className="text-3xl lg:text-4xl font-bold tabular-nums text-indigo-300">{String(monthlyTimeLeft.hours).padStart(2, '0')}</div>
                  <div className="text-xs text-slate-300 mt-1 font-medium">{getRussianDeclension(monthlyTimeLeft.hours, ['час', 'часа', 'часов'])}</div>
                </div>
                <div className="text-2xl lg:text-3xl text-purple-400 pb-3 animate-pulse delay-100 font-bold">:</div>
                <div className="min-w-[70px] px-4 py-3 rounded-2xl bg-gradient-to-b from-slate-700/50 to-slate-800/50 border border-slate-600/30 shadow-lg backdrop-blur-sm">
                  <div className="text-3xl lg:text-4xl font-bold tabular-nums text-purple-300">{String(monthlyTimeLeft.minutes).padStart(2, '0')}</div>
                  <div className="text-xs text-slate-300 mt-1 font-medium">минут</div>
                </div>
                <div className="text-2xl lg:text-3xl text-purple-400 pb-3 animate-pulse delay-200 font-bold">:</div>
                <div className="min-w-[70px] px-4 py-3 rounded-2xl bg-gradient-to-b from-slate-700/50 to-slate-800/50 border border-slate-600/30 shadow-lg backdrop-blur-sm">
                  <div className="text-3xl lg:text-4xl font-bold tabular-nums text-indigo-300">{String(monthlyTimeLeft.seconds).padStart(2, '0')}</div>
                  <div className="text-xs text-slate-300 mt-1 font-medium">секунд</div>
                </div>
              </>
            )}
          </motion.div>

          {/* Button Container */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex items-center justify-center"
          >
            <motion.button
              whileHover={{
                scale: (isParticipatingMonthly || isMonthlyCompleted || isLoadingMonthly) ? 1.0 : 1.05,
                y: (isParticipatingMonthly || isMonthlyCompleted || isLoadingMonthly) ? 0 : -2
              }}
              whileTap={{ scale: (isParticipatingMonthly || isMonthlyCompleted || isLoadingMonthly) ? 1.0 : 0.97 }}
              disabled={isParticipatingMonthly || isLoadingMonthly || isMonthlyCompleted}
              onClick={handleJoinMonthlyRaffle}
              className={`relative px-8 lg:px-12 py-4 lg:py-5 rounded-2xl font-bold text-lg lg:text-xl transition-all duration-300 shadow-xl hover:shadow-2xl focus:outline-none focus:ring-2 focus:ring-purple-400/50 ${
                isMonthlyCompleted
                  ? 'bg-gradient-to-r from-slate-600/30 to-slate-700/30 text-slate-400 border border-slate-600/30'
                  : isParticipatingMonthly
                    ? 'bg-gradient-to-r from-slate-600/40 to-slate-700/40 text-slate-300 border border-slate-500/40'
                    : 'bg-gradient-to-r from-purple-500 via-purple-600 to-indigo-500 text-white hover:from-purple-600 hover:via-purple-700 hover:to-indigo-600 border border-purple-400/30 hover:border-purple-300/50'
              }`}
            >
              <span className="mr-2 sm:mr-3">🎟️</span>
              {isMonthlyCompleted
                ? 'Розыгрыш завершен'
                : isLoadingMonthly
                  ? 'Отправка...'
                  : isParticipatingMonthly
                    ? 'Вы уже участвуете'
                    : 'Участвовать'
              }
              {!isParticipatingMonthly && !isLoadingMonthly && !isMonthlyCompleted && (
                <span className="absolute inset-0 rounded-xl sm:rounded-2xl ring-1 ring-white/10" />
              )}
            </motion.button>
          </motion.div>

          {/* Participants Container */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="flex items-center justify-center"
          >
            <div className="text-sm text-slate-300 font-medium">
              👥 Участников: <span className="text-purple-300 font-bold">{monthlyParticipantsCount}</span>
            </div>
          </motion.div>
        </div>
      </motion.div>
        )}
      </div>
    </>
  )
}

export default RaffleWidget
